type BrandingThemeInput = {
  colorPrimario?: string | null;
  colorSecundario?: string | null;
  temaOscuro?: boolean | null;
};

const DEFAULT_PRIMARY = '#314a25';
const DEFAULT_SECONDARY = '#bfa98d';

const isValidHex = (value: string) => /^#[0-9a-fA-F]{6}$/.test(value);

const normalizeHex = (value: string | null | undefined) => {
  const v = String(value ?? '').trim();
  return isValidHex(v) ? v.toLowerCase() : null;
};

const hexToRgb = (hex: string) => {
  const raw = hex.replace('#', '');
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return { r, g, b };
};

const rgbToHex = (r: number, g: number, b: number) => {
  const to = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
};

const mix = (a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }, t: number) => {
  const tt = Math.max(0, Math.min(1, t));
  return {
    r: a.r + (b.r - a.r) * tt,
    g: a.g + (b.g - a.g) * tt,
    b: a.b + (b.b - a.b) * tt,
  };
};

const lighten = (rgb: { r: number; g: number; b: number }, amount: number) => {
  return mix(rgb, { r: 255, g: 255, b: 255 }, amount);
};

const darken = (rgb: { r: number; g: number; b: number }, amount: number) => {
  return mix(rgb, { r: 0, g: 0, b: 0 }, amount);
};

export function applyBrandingTheme(input: BrandingThemeInput) {
  if (typeof document === 'undefined') return;

  const primary = normalizeHex(input.colorPrimario) ?? DEFAULT_PRIMARY;
  const secondary = normalizeHex(input.colorSecundario) ?? DEFAULT_SECONDARY;
  const dark = !!input.temaOscuro;

  const root = document.documentElement;
  const p = hexToRgb(primary);
  const s = hexToRgb(secondary);
  const p50 = lighten(p, 0.92);
  const p100 = lighten(p, 0.84);
  const p400 = lighten(p, 0.12);
  const p600 = darken(p, 0.14);
  const p700 = darken(p, 0.26);
  const s600 = darken(s, 0.14);

  // Expose a small palette derived from the chosen colors so we can override Tailwind teal utilities.
  root.style.setProperty('--brand-primary', primary);
  root.style.setProperty('--brand-secondary', secondary);
  root.style.setProperty('--brand-primary-rgb', `${p.r} ${p.g} ${p.b}`);
  root.style.setProperty('--brand-secondary-rgb', `${s.r} ${s.g} ${s.b}`);

  root.style.setProperty('--brand-primary-50', rgbToHex(p50.r, p50.g, p50.b));
  root.style.setProperty('--brand-primary-100', rgbToHex(p100.r, p100.g, p100.b));
  root.style.setProperty('--brand-primary-400', rgbToHex(p400.r, p400.g, p400.b));
  root.style.setProperty('--brand-primary-500', primary);
  root.style.setProperty('--brand-primary-600', rgbToHex(p600.r, p600.g, p600.b));
  root.style.setProperty('--brand-primary-700', rgbToHex(p700.r, p700.g, p700.b));

  root.style.setProperty('--brand-secondary-500', secondary);
  root.style.setProperty('--brand-secondary-600', rgbToHex(s600.r, s600.g, s600.b));

  root.classList.toggle('dark', dark);
  // Helps native form controls align with the theme.
  root.style.setProperty('color-scheme', dark ? 'dark' : 'light');
}
